import { NAMES } from "@/scripts/components/ab-specification-picker/constants/abSpecificationPicker.constants";
import { AbLoaderInModal } from "@/scripts/components/ab-loader/ab-loader-in-modal.class";
import { ProductNameResponseItemDTO } from "@shared/DTO";
import ProductsApi from "@/scripts/api/products.api";
import { createDomElementHandler } from "@/scripts/utils/dom.utils";
import {
  AbSelectChangeDetail,
  AbSelectElement,
  AbSelectOption,
  AbSelectSearchDetail,
} from "@/scripts/components/ab-select/ab-select.types";
import { ProductTypeEnum } from "@shared/enums";
import { filterOptionsByTerm } from "@/scripts/utils/filterProductByNames.util";
import { AbDataSheetInModalClass } from "@/scripts/components/ab-data-sheet/ab-data-sheet-in-modal.class";
import "./abSpecificationPicker.style.scss";

/**
 * Custom element <ab-specification-picker>
 *
 * Attributi:
 *  - product-types: lista di ProductTypeEnum separata da virgola (es. "mirrorless,reflex")
 *
 * Al click su un prodotto apre la sua scheda tecnica in una modale.
 */
export class AbSpecificationPickerElement extends HTMLElement {

  private _productApiService = ProductsApi;

  #initialized = false;
  #loader: AbLoaderInModal;
  #allProducts: ProductNameResponseItemDTO[] = [];
  #allOptions: AbSelectOption[] = [];
  #selectEl: AbSelectElement | null = null;

  constructor() {
    super();
    this.#loader = new AbLoaderInModal();
  }

  async connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;
    await this.init();
  }

  /** Legge i product-types dall'attributo e restituisce l'array normalizzato */
  private getProductTypes(): ProductTypeEnum[] {
    const raw = this.getAttribute("product-types") ?? "";
    return raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean) as ProductTypeEnum[];
  }

  private async init() {
    const productTypes = this.getProductTypes();
    if (!productTypes.length) return;

    this.classList.add(NAMES.CLASS_ROOT);

    this.#loader.start();
    const productNames = await this._productApiService.getProductNames({
      type: productTypes,
      visibility: ["showDataSheet"],
    });
    this.#loader.stop();

    this.#allProducts = productNames;

    this.#allOptions = productNames
      .map((product) => {
        const { brand, names } = product?.productData || {};
        const group = brand?.length ? `Fotocamere ${brand}` : "";
        return {
          label: names?.[0],
          id: product._id,
          group,
        };
      });

    this.buildDom();
  }

  private buildDom() {
    const fragment = document.createDocumentFragment();

    const selectWrapper = createDomElementHandler("div", NAMES.CLASS_SELECT_WRAPPER);

    this.#selectEl = createDomElementHandler(
      "ab-select",
      "ab-specification-picker__select",
      { "font-size": "sm", "dropdown": "" }
    ) as AbSelectElement;

    this.#selectEl.options = this.#allOptions;

    this.#selectEl.addEventListener("ab-select-search", (event: Event) => {
      const term = (
        (event as CustomEvent<AbSelectSearchDetail>).detail?.term ?? ""
      ).toLowerCase();
      this.#selectEl!.options = term.length
        ? filterOptionsByTerm(term, this.#allOptions, this.#allProducts)
        : this.#allOptions;
    });

    this.#selectEl.addEventListener("ab-select-change", (event: Event) => {
      const { option } = (event as CustomEvent<AbSelectChangeDetail>).detail ?? {};
      if (!option?.id) return;
      this.openDataSheet(option.id);
      // Resetta input, pannello e ripristina l'elenco completo
      this.#selectEl?.clear();
      this.#selectEl!.options = this.#allOptions;
    });

    selectWrapper.appendChild(this.#selectEl);
    fragment.appendChild(selectWrapper);
    this.replaceChildren(fragment);
  }

  private openDataSheet(productId: string) {
    new AbDataSheetInModalClass({ productIds: productId, autoOpen: false });
  }
}

export function defineAbSpecificationPickerElement() {
  const TAG_NAME = NAMES.TAG_NAME;
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, AbSpecificationPickerElement);
}



